import { AppBar } from "@/elements/components/ui/app-bar";
import { Button } from "@/elements/components/ui/button";
import { OTPInput } from "@/elements/components/ui/otp-input";
import { Redirect, useLocalSearchParams, useRouter } from "expo-router";
import React, { useState } from "react";
import { ScrollView, Text, View } from "react-native";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useAuthActions } from "@convex-dev/auth/react";
import { handleAuthError } from "@/elements/components/utils/auth-error-handler";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { auth } from "@/elements/components/utils/convex-api";
import Loader from "@/elements/components/shared/loader";
import ResponsiveContent from "@/elements/components/shared/responsive-content";
import Toast from "react-native-toast-message";

const VerifySchema = z.object({
  code: z.string().length(8, "Code must be 8 digits"),
});

type VerifySchemaType = z.infer<typeof VerifySchema>;

function VerifyEmailScreen() {
  const router = useRouter();
  const { email, persona } = useLocalSearchParams<{
    email?: string;
    persona?: "parent" | "tutor" | "student";
  }>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { handleSubmit, setValue, formState: { isValid, errors } } = useForm({
    defaultValues: { code: "" },
    mode: "onChange",
    resolver: zodResolver(VerifySchema),
  });
  const { signIn } = useAuthActions();

  const { data: isAuthenticated = false, isSuccess } = useQuery(
    convexQuery(auth.isAuthenticated, {})
  );

  if (!persona) {
    return <Redirect href={"/(auth)/sign-in-options"} />;
  }

  if (!isSuccess) {
    return <Loader />;
  }

  if (isAuthenticated) {
    return <Redirect href={"/(app)/home"} />;
  }

  const onSubmit = async (data: VerifySchemaType) => {
    try {
      if (!email) {
        Toast.show({
          type: "error",
          text1: "Missing email for verification",
          text1Style: { fontFamily: "Geologica_400Regular", fontSize: 10, color: "#000000" },
        });
        return;
      }
      setIsSubmitting(true);
      await signIn("password", {
        code: data.code.trim(),
        email: (email as string).toLowerCase().trim(),
        flow: "email-verification",
      });
      // Successful verification will sign the user in and unmount this screen
    } catch (error) {
      const errorResult = handleAuthError(error);
      console.log(errorResult);
      Toast.show({
        type: "error",
        text1: errorResult.message,
        text1Style: { fontFamily: "Geologica_400Regular", fontSize: 10, color: "#000000" },
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOTPComplete = (code: string) => {
    setValue("code", code, { shouldValidate: true, shouldDirty: true });
  };

  const handleOTPChange = (code: string) => {
    setValue("code", code, { shouldValidate: true, shouldDirty: true });
  };

  return (
    <View className="flex-1 bg-teachLoop-creamLight">
      <AppBar>
        <AppBar.Icon
          onPress={() =>
            router.canGoBack()
              ? router.back()
              : router.replace({ pathname: "/(auth)/sign-in", params: { persona } })
          }
        />
        <AppBar.Title>Back</AppBar.Title>
      </AppBar>
      <ScrollView showsVerticalScrollIndicator={false}>
        <ResponsiveContent>
          <View className="mx-5">
            <View className="h-4" />
            <View className="gap-1">
              <Text className="font-zilla-slab-medium leading-[1.2] font-medium text-[28px] text-black">
                Enter verification code
              </Text>
              <Text className="font-geologica-regular font-normal text-[20px] text-black leading-[1.5] ">
                We sent a code to {email}
              </Text>
            </View>
            <View className="h-8" />
            <View className="gap-4">
              <OTPInput
                length={8}
                onComplete={handleOTPComplete}
                onChangeText={handleOTPChange}
                error={!!errors.code}
              />
            </View>
            <View className="h-8" />
            <Button
              className="w-full"
              size="md"
              intent="primary"
              onPress={handleSubmit(onSubmit)}
              disabled={!isValid || isSubmitting}
            >
              <Button.Text size="md">Continue</Button.Text>
            </Button>
            <View className="h-8" />
          </View>
        </ResponsiveContent>
      </ScrollView>
    </View>
  );
}

export default VerifyEmailScreen;


